import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
// import { useTranslation } from 'react-i18next'; // Not used in this component
import LanguageSwitcher from '../components/LanguageSwitcher';
import otpService from '../services/otpService';

interface OTPVerificationPageProps {
  customerData?: {
    fullName: string;
    mobileNumber: string;
  };
  onOTPVerified?: (data: { fullName: string; mobileNumber: string }) => void;
}

const OTPVerificationPage: React.FC<OTPVerificationPageProps> = ({ 
  customerData: propCustomerData, 
  onOTPVerified 
}) => {
  // const { t } = useTranslation(); // Translation not used in this component
  const navigate = useNavigate();
  const location = useLocation();
  
  // Get customer data from navigation state or props
  const customerData = location.state?.customerData || propCustomerData;
  
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [resendTimer, setResendTimer] = useState(0);
  const [canResend, setCanResend] = useState(false);
  
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  // Auto-focus first input on mount
  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  // Resend timer countdown
  useEffect(() => {
    if (resendTimer > 0) {
      const timer = setTimeout(() => {
        setResendTimer(resendTimer - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [resendTimer]);

  // Start resend timer on component mount
  useEffect(() => {
    setResendTimer(30); // 30 seconds timer
  }, []);

  const handleOTPChange = (index: number, value: string) => {
    // Only allow single digit
    if (value.length > 1) return;
    
    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);
    setError('');

    // Auto-focus next input
    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text').replace(/\D/g, '').slice(0, 6);
    const newOtp = [...otp];
    
    for (let i = 0; i < pastedData.length && i < 6; i++) {
      newOtp[i] = pastedData[i];
    }
    
    setOtp(newOtp);
    setError('');
    
    // Focus the next empty input or last input
    const nextIndex = Math.min(pastedData.length, 5);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 6) {
      setError('Please enter complete 6-digit OTP');
      return;
    }

    if (!/^\d{6}$/.test(otpString)) {
      setError('Please enter valid 6-digit OTP');
      return;
    }

    if (!customerData?.mobileNumber) {
      setError('Mobile number not found. Please go back and try again.');
      return;
    }

    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('🔐 Verifying OTP for:', customerData.mobileNumber);
      
      // Call OTP verify API
      const response = await otpService.verifyOTP({
        mobileNumber: customerData.mobileNumber,
        otp: otpString
      });

      if (response.success) {
        console.log('✅ OTP verified successfully:', response.data);
        setMessage('OTP verified successfully!');
        
        // Navigate to next page immediately
        if (onOTPVerified) {
          onOTPVerified(customerData!);
        } else {
          navigate('/enter-amount', { 
            state: { 
              customerData,
              donationData: location.state?.donationData 
            } 
          });
        }
      } else {
        console.error('❌ Failed to verify OTP:', response.error);
        setError(response.error || 'Invalid OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ Error verifying OTP:', error);
      setError(error.message || 'Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleResendOTP = async () => {
    if (!canResend || !customerData?.mobileNumber || !customerData?.fullName) return;

    setLoading(true);
    setError('');
    setMessage('');

    try {
      console.log('🔄 Resending OTP for:', customerData.mobileNumber);
      
      // Call OTP resend API
      const response = await otpService.resendOTP({
        mobileNumber: customerData.mobileNumber,
        fullName: customerData.fullName
      });

      if (response.success) {
        console.log('✅ OTP resent successfully:', response.data);
        setMessage('OTP sent successfully to +91 ' + customerData.mobileNumber);
        setResendTimer(30);
        setCanResend(false);
        setOtp(['', '', '', '', '', '']);
        
        // Focus first input
        inputRefs.current[0]?.focus();
      } else {
        console.error('❌ Failed to resend OTP:', response.error);
        setError(response.error || 'Failed to resend OTP. Please try again.');
      }
    } catch (error: any) {
      console.error('❌ Error resending OTP:', error);
      setError(error.message || 'Network error. Please check your connection and try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBackToVerification = () => {
    navigate('/verification');
  };

  if (!customerData) {
    return (
      <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
        <div className="text-center px-4">
          <h2 className="text-lg sm:text-xl md:text-2xl font-bold text-white mb-3 sm:mb-4">No Customer Data Found</h2>
          <button
            onClick={() => navigate('/verification')}
            className="bg-orange-500 hover:bg-orange-600 text-white font-semibold py-2 sm:py-3 px-4 sm:px-6 rounded-lg transition-colors text-sm sm:text-base"
          >
            Go to Verification
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black/20 backdrop-blur-sm flex items-center justify-center" style={{backgroundImage: 'url(/images/Asset5.png)', backgroundSize: 'cover', backgroundPosition: 'center', backgroundRepeat: 'no-repeat', backgroundAttachment: 'fixed'}}>
      <div className="w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-2xl mx-auto px-4 py-4 sm:py-6 md:py-8">
        {/* Language Switcher */}
        <div className="absolute top-4 right-4 z-10">
          <LanguageSwitcher />
        </div>

        {/* Main Card */}
        <div className="bg-white rounded-2xl shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-orange-400 to-orange-500 p-4 sm:p-6 text-center">
            <div className="flex items-center justify-center mb-3">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-white rounded-full border-2 border-white flex items-center justify-center">
                <img 
                  src="/images/Asset2.png" 
                  alt="Temple Logo" 
                  className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                />
              </div>
            </div>
            <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white">श्री मार्तंड देवसंस्थान - जेजुरी</h1>
            <p className="text-white/90 text-xs sm:text-sm mt-1">OTP Verification • जेजुरी</p>
          </div>

          {/* Content */}
          <div className="p-4 sm:p-6 space-y-4 sm:space-y-6">
            {/* Customer Info */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4">
              <div className="flex items-center mb-2 sm:mb-3">
                <span className="text-blue-600 text-base sm:text-lg mr-2">📱</span>
                <h3 className="text-base sm:text-lg font-semibold text-blue-800">Verify Mobile Number</h3>
              </div>
              <div className="space-y-1 text-xs sm:text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-600">Name:</span>
                  <span className="font-semibold text-gray-800">{customerData.fullName}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-600">Mobile:</span>
                  <span className="font-semibold text-gray-800">+91 {customerData.mobileNumber}</span>
                </div>
              </div>
            </div>

            {/* Form Title */}
            <div className="text-center mb-4 sm:mb-6">
              <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1 sm:mb-2">Enter Verification Code</h2>
              <p className="text-sm sm:text-base text-gray-600">We've sent a 6-digit code to your mobile number</p>
            </div>

            {/* OTP Display - Only show if OTP is available */}
            {location.state?.otpResponse?.otp && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 sm:p-4 text-center">
                <h3 className="text-blue-800 font-bold mb-2 text-sm sm:text-base flex items-center justify-center">
                  <span className="mr-2">📱</span>
                  VERIFICATION CODE
                </h3>
                <p className="text-blue-700 text-xs sm:text-sm mb-2">Your OTP is:</p>
                <div className="bg-blue-100 rounded-lg p-2 sm:p-3">
                  <span className="text-blue-800 text-2xl sm:text-3xl font-bold tracking-widest">
                    {location.state.otpResponse.otp}
                  </span>
                </div>
                <p className="text-blue-700 text-xs mt-2">Enter this code to verify your mobile number</p>
              </div>
            )}

            {/* OTP Input */}
            <div className="space-y-3 sm:space-y-4">
              <div className="flex justify-center space-x-2 sm:space-x-3">
                {otp.map((digit, index) => (
                  <input
                    key={index}
                    ref={(el) => (inputRefs.current[index] = el)}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleOTPChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={handlePaste}
                    className={`w-10 h-10 sm:w-12 sm:h-12 text-center text-lg sm:text-2xl font-bold border-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 transition-colors ${
                      error ? 'border-red-500' : 'border-gray-300 focus:border-orange-500'
                    }`}
                  />
                ))}
              </div>
              
              {error && (
                <p className="text-red-600 text-xs sm:text-sm text-center">{error}</p>
              )}
              
              {message && (
                <p className="text-green-600 text-xs sm:text-sm text-center">{message}</p>
              )}
            </div>

            {/* Resend OTP */}
            <div className="text-center">
              {canResend ? (
                <button
                  onClick={handleResendOTP}
                  disabled={loading}
                  className="text-orange-600 hover:text-orange-700 font-medium text-xs sm:text-sm disabled:opacity-50"
                >
                  Resend OTP
                </button>
              ) : (
                <p className="text-gray-500 text-xs sm:text-sm">
                  Resend OTP in {resendTimer} seconds
                </p>
              )}
            </div>

            {/* Action Buttons */}
            <div className="space-y-2 sm:space-y-3">
              <button
                onClick={handleVerifyOTP}
                disabled={loading || otp.join('').length !== 6}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 disabled:from-gray-400 disabled:to-gray-500 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors flex items-center justify-center text-sm sm:text-base"
              >
                {loading ? (
                  <div className="animate-spin rounded-full h-4 w-4 sm:h-5 sm:w-5 border-b-2 border-white mr-2"></div>
                ) : null}
                Verify OTP
              </button>
              
              <button
                onClick={handleBackToVerification}
                className="w-full bg-gray-600 hover:bg-gray-700 text-white font-semibold py-2 sm:py-3 px-4 rounded-lg transition-colors text-sm sm:text-base"
              >
                Back to Verification
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="text-center mt-4 sm:mt-6 md:mt-8">
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            {/* AUGMONT Logo */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">Powered By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/Asset1.png" 
                    alt="AUGMONT GOLD" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
            
            {/* Mandirsetu Initiative */}
            <div className="bg-white rounded-lg px-3 sm:px-4 py-2 inline-block">
              <div className="flex flex-col items-center justify-center h-[88px] w-[240px]">
                <p className="text-xs sm:text-sm text-gray-700 mb-1 font-medium">An Initiative By</p>
                <div className="h-[72px] w-[240px] flex items-center justify-center mx-auto">
                  <img 
                    src="/images/mandirSetu.png" 
                    alt="An Initiative By Mandirsetu" 
                    className="h-full w-full object-contain"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OTPVerificationPage;
